import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  Contact,
  Message,
  Booking,
  Escalation,
  ToolCall,
  apiResult,
  Company
} from "../interfaces";
import {
  adaptItem,
  howMany,
} from "../utils/contacts";

function mergeMessages(
  newMessages: Message[],
  previousMessages: Message[]
): Message[] {
  const prevMap = new Map<string, Message>(
    previousMessages.map(m => [m.id!, m])
  )

  const mergedHistory = newMessages.map(msg => {
    const prev = prevMap.get(String(msg.id))
    if (!prev) return msg
    return {
      ...prev,
      ...msg
    }
  })

  const newIds = new Set(newMessages.map(m => m.id))
  const remaining = previousMessages.filter(m => !newIds.has(m.id))

  return [...mergedHistory, ...remaining]
}

const adaptNewContact = (newContact: apiResult, previousMessages: Message[] = [] ): 
  Pick<
    ContactStoreState,
    "contact" | "messages" | "bookings" | "escalations" | "companies" | "toolCalls"
  > => {
  const properties = howMany(newContact.messages, ["user", "assistant"]);
  const { contact, messages: newMessages, bookings, escalations, companies, toolCalls } =
    adaptItem(newContact, properties);
  const messages = mergeMessages(newMessages, previousMessages)

  return {
    contact,
    messages,
    bookings,
    escalations,
    companies,
    toolCalls,
  };
};

export interface ContactData {
  loading: boolean;
  id: string;
  contact: Contact;
  messages: Message[],
  bookings: Booking[],
  escalations: Escalation[],
  toolCalls: ToolCall[],
  companies: Company[],
}

interface ContactStoreState extends ContactData {
  setContactId: (id: string) => void;
  setLoading: (loading: boolean) => void;
  setContact: (contact: apiResult) => void;
  setContactPreview: (contact: ContactData) => void;
  newMessage: (contacts: apiResult[]) => void;
  reset: () => void;
}

const contactInitialState: ContactData = {
  id: "",
  loading: false,
  contact: {
    type: "list",
    id: "",
    name: "",
    preview: "",
    unread: 0,
    timestamp: "",
    enabled: false,
    optedOut: false,
    manual: false,
    blocked: false,
    phoneNumber: "",
    status: "",
    escalated: false,
    isBook: false,
    isManual: false,
    isPayment: false,
    isBooked: false,
    isOnline: false,
    timestampNative: new Date().getTime(),
    count: 0,
    followers: 0,
    username: "",
    companies: [],
  },
  messages: [],
  bookings: [],
  escalations: [],
  toolCalls: [],
  companies: [],
}

const useContactStore = create(
  devtools<ContactStoreState>((set, get) => ({
    ...contactInitialState,
    
    setContactId: (id) => {
      const { id: stateContactId } = get() || {};
      return set({
        ...(stateContactId !== id ? contactInitialState : {}),
        id,
      });
    },
    setLoading: (loading) => set({ loading }),
    setContact: (newContact) => {
      const { messages = [] } = get() || {};
      const adaptedContact = adaptNewContact(newContact, messages);

      return set({
        id: newContact._id,
        loading: false,
        ...adaptedContact,
      })
    },
    setContactPreview: (contact) => set({
      ...contact,
    }),
    newMessage: (contacts) => {
      const state = get() || {};
      const matches = contacts.filter((c: any) => c._id === state.id);
      if (matches.length === 1) {
        const [match] = matches;
        const adaptedContact = adaptNewContact(match);
        return set({ ...adaptedContact })
      }
    },
    reset: () => set({ ...contactInitialState })
  }))
);

const selectContactData = (state: ContactStoreState) => ({
  id: state.id,
  loading: state.loading,
  contact: state.contact,
  messages: state.messages,
  bookings: state.bookings,
  escalations: state.escalations,
  toolCalls: state.toolCalls,
  companies: state.companies,
});

const selectContactActions = (state: ContactStoreState) => ({
  setContactId: state.setContactId,
  setLoading: state.setLoading,
  setContact: state.setContact,
  setContactPreview: state.setContactPreview,
  newMessage: state.newMessage,
  resetContact: state.reset,
})

export const useContactData = () =>
  useContactStore(selectContactData);

export const useContactActions = () =>
  useContactStore(selectContactActions);

export default useContactStore;
